// @flow

import React, { useReducer, Fragment } from 'react';
import { connect } from 'react-redux';
import BoardActivityPinRepComment from 'app/common/react/components/BoardActivities/BoardActivityPinRepComment';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import { createActivityItem } from 'app/common/redux/actions/activityItem/createActivityItem';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { type BoardActivityItem } from 'app/common/redux/reducers/activityItems';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { type State as ReduxState } from 'app/common/redux/reducers/types';
import { type Viewer } from 'app/common/lib/react/withViewer';
import { Avatar, Box, IconButton, Text } from 'gestalt';
import { compose, bindActionCreators, type Dispatch } from 'redux';

const MAX_CHAR_LENGTH = 2000;
const MIN_ROWS = 1;
const MAX_ROWS = 2;
const TEXT_AREA_LINE_HEIGHT = 17;

const commonTextAreaStyle = {
  background: 'transparent',
  border: '0',
  display: 'inline-block',
  fontSize: '14px',
  lineHeight: `${TEXT_AREA_LINE_HEIGHT}px`,
  maxHeight: '34px',
  outline: '0',
  overflowX: 'hidden',
  resize: 'none',
  whiteSpace: 'pre-wrap',
  width: '100%',
  wordBreak: 'break-all',
};

type OwnProps = {|
  pin: Pin,
|};

type ReduxDispatchProps = {|
  createActivityItem: Function,
|};

type ReduxStateProps = {|
  boardActivityComments: Array<BoardActivityItem>,
  parentActivity: ?BoardActivityItem,
  viewer: Viewer,
|};

type ConnectedProps = {|
  ...ReduxStateProps,
  ...ReduxDispatchProps,
  ...OwnProps,
|};

type Props = {| ...ReduxStateProps, ...ReduxDispatchProps, ...OwnProps |};

type State = {|
  commentValue: string,
  commentAdded: boolean,
  inputRowsValue: number,
  showMoreComment: boolean,
|};

type StateAction = {|
  type: number,
  commentValue?: string,
  commentAdded?: boolean,
  inputRowsValue?: number,
|};

const stateActionTypes = {
  SET_COMMENT_VALUE: 0,
  SET_INPUT_ROWS_VALUE: 1,
  COMMENT_ADDED: 2,
};

const initialCommentValue = '';
const initialCommentAdded = false;
const initialInputRowsValue = MIN_ROWS;

function reducer(state: State, action: StateAction) {
  switch (action.type) {
    case stateActionTypes.SET_COMMENT_VALUE: {
      return { ...state, commentValue: action.commentValue || initialCommentValue };
    }
    case stateActionTypes.SET_INPUT_ROWS_VALUE: {
      return { ...state, inputRowsValue: action.inputRowsValue || initialInputRowsValue };
    }
    case stateActionTypes.COMMENT_ADDED: {
      return {
        ...state,
        commentValue: initialCommentValue,
        commentAdded: true,
        inputRowsValue: initialInputRowsValue,
      };
    }
    default: {
      return state;
    }
  }
}

// Renders board activity comments under Pin Reps in group board pin grid
export function BoardActivityPinRepComments({
  boardActivityComments,
  createActivityItem,
  parentActivity,
  pin,
  viewer,
}: Props) {
  const [state, dispatch] = useReducer(reducer, {
    commentValue: initialCommentValue,
    commentAdded: initialCommentAdded,
    inputRowsValue: initialInputRowsValue,
    showMoreComment: !!parentActivity && parentActivity.comment_count > 3,
  });

  const handleChange = (event) => {
    dispatch({
      type: stateActionTypes.SET_COMMENT_VALUE,
      commentValue: String(event.target.value),
    });
    if (event && event.target) {
      const previousRows = event.target.rows;
      event.target.rows = MIN_ROWS;
      const currentRows = Math.floor(event.target.scrollHeight / TEXT_AREA_LINE_HEIGHT);
      if (currentRows === previousRows) {
        event.target.rows = currentRows;
      }
      if (currentRows >= MAX_ROWS) {
        event.target.rows = MAX_ROWS;
        event.target.scrollTop = event.target.scrollHeight;
      }
      dispatch({
        type: stateActionTypes.SET_INPUT_ROWS_VALUE,
        inputRowsValue: currentRows < MAX_ROWS ? currentRows : MAX_ROWS,
      });
    }
  };

  const handleAddComment = () => {
    if (!parentActivity) {
      return;
    }
    const tags = JSON.stringify([]);
    createActivityItem({
      imageSignatures: '',
      objectIds: [],
      parentId: parentActivity.id,
      parentType: parentActivity.type,
      text: state.commentValue,
      tags,
    });
    dispatch({ type: stateActionTypes.COMMENT_ADDED });
  };

  if (!viewer.isAuth || !boardActivityComments || boardActivityComments.length === 0) {
    return null;
  }

  const hasInputValue = state.commentValue.length > 0;
  const textAreaStyle = {
    color: hasInputValue ? 'black' : '#8e8e8e',
    ...commonTextAreaStyle,
  };

  const firstCommentId = boardActivityComments.length > 0 ? boardActivityComments[0].id : null;
  const secondCommentId = boardActivityComments.length > 1 ? boardActivityComments[1].id : null;
  const thirdCommentId = boardActivityComments.length > 2 ? boardActivityComments[2].id : null;

  const firstCommentHasReply = boardActivityComments[0].comment_count > 0;
  const secondCommentHasReply = secondCommentId && boardActivityComments[1].comment_count > 0;

  const showReplyForFirstComment = firstCommentHasReply;
  const showReplyForSecondComment = secondCommentHasReply && !showReplyForFirstComment;
  const showThirdComment =
    thirdCommentId && !showReplyForFirstComment && !showReplyForSecondComment;

  /* Compute "more comments" count number */
  const maxShownCommentsCount = showThirdComment ? 3 : 2;
  const shownCommentsCount =
    boardActivityComments.length < 3 ? boardActivityComments.length : maxShownCommentsCount;
  const moreCommentsCount = parentActivity ? parentActivity.comment_count - shownCommentsCount : 0;

  const moreCommentsText = (
    <Text size="xs" weight="bold">
      {interpolateNamedTemplate(
        i18n.ngettext(
          '{{ moreCommentsCount }} more comment',
          '{{ moreCommentsCount }} more comments',
          moreCommentsCount,
          'Number of more comments indicator for board comments under pin cards',
        ),
        { moreCommentsCount },
      )}
    </Text>
  );

  const viewCommentText = (
    <Box marginStart={2}>
      <Text size="sm" weight="bold">
        {i18n._('View your comment', 'Text after user added comments under group board pin grid')}
      </Text>
    </Box>
  );

  const comments = (
    <Box display="flex" direction="column" width="100%">
      {state.showMoreComment &&
        !!moreCommentsCount && (
          <Box marginTop={2} marginBottom={1}>
            {parentActivity && parentActivity.pin ? (
              <NavigateLink to={`/pin/${parentActivity.pin.id}/`}>{moreCommentsText}</NavigateLink>
            ) : (
              moreCommentsText
            )}
          </Box>
        )}
      {showThirdComment && (
        <BoardActivityPinRepComment
          commentActivityId={thirdCommentId}
          parentActivity={parentActivity}
        />
      )}
      {secondCommentId && (
        <BoardActivityPinRepComment
          commentActivityId={secondCommentId}
          parentActivity={parentActivity}
          showReplies={showReplyForSecondComment}
        />
      )}
      {/* Newest comment at bottom */}
      <BoardActivityPinRepComment
        commentActivityId={firstCommentId}
        parentActivity={parentActivity}
        showReplies={showReplyForFirstComment}
      />
    </Box>
  );

  return (
    <Box display="flex" direction="column">
      {boardActivityComments.length > 0 && comments}
      <Box alignItems="center" display="flex" direction="row" marginTop={2}>
        <Box height={32} minWidth={32}>
          <Avatar src={viewer.imageMediumUrl} name={viewer.username} />
        </Box>
        {state.commentAdded &&
          (parentActivity && parentActivity.pin ? (
            <NavigateLink to={`/pin/${parentActivity.pin.id}/`}>{viewCommentText}</NavigateLink>
          ) : (
            viewCommentText
          ))}
        {!state.commentAdded && (
          <Fragment>
            <Box
              dangerouslySetInlineStyle={{
                __style: { borderRadius: '16px', backgroundColor: '#efefef' },
              }}
              width="100%"
              alignItems="center"
              display="flex"
              marginLeft={2}
              marginRight={2}
              paddingX={4}
              paddingY={1}
            >
              {/* Use textarea here to address special design requirement:
                  the comment input area need to be gray, have larger border-radius, and borderless */}
              <textarea
                maxLength={MAX_CHAR_LENGTH}
                name="commentTextBox"
                placeholder={i18n._(
                  'Add comment',
                  'Placeholder for comment text field under pin rep',
                )}
                style={textAreaStyle}
                rows={state.inputRowsValue}
                value={state.commentValue}
                onChange={handleChange}
              />
            </Box>
            {hasInputValue && (
              <Box width={32} height={32} shape="circle" color="red">
                <IconButton
                  accessibilityLabel={i18n._(
                    'send',
                    'Accessibility label for send comment under pin rep',
                  )}
                  bgColor="transparent"
                  icon="send"
                  iconColor="white"
                  size="sm"
                  onClick={handleAddComment}
                />
              </Box>
            )}
          </Fragment>
        )}
      </Box>
    </Box>
  );
}

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps => ({
  createActivityItem: bindActionCreators(createActivityItem, dispatch),
});

const mapStateToProps = (
  { activityItems, groupBoardGridComments, viewer }: ReduxState,
  { pin: { board_activity: boardActivity } }: OwnProps,
): ReduxStateProps => {
  const boardActivityId = boardActivity && boardActivity.id;
  const parentActivity = boardActivityId ? activityItems[boardActivityId] : null;
  const boardActivityCommentIds = boardActivityId
    ? groupBoardGridComments[boardActivityId] || []
    : [];
  const boardActivityComments = boardActivityCommentIds.map(
    (activityId) => activityItems[activityId],
  );
  return {
    boardActivityComments,
    parentActivity,
    viewer,
  };
};

export default compose(
  connect<ConnectedProps, OwnProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(BoardActivityPinRepComments);
